<template>
    <div class="about">
        <UIGridContainer>
            <AboutBanner />
            <UIGridRow class="top-spacing justify-content-center mt-md-5">
                <UIGridCol md="9" lg="5">
                    <div class="info-text">
                        <h1>{{ translate('paragraph1.title') }}</h1>
                        <span v-html="translate('paragraph1.content')" />
                        <h2>{{ translate('paragraph1.title2') }}</h2>
                        <p>{{ translate('paragraph1.content2') }}</p>
                    </div>
                </UIGridCol>
                <UIGridCol cols="10" lg="7" class="mt-5">
                    <nuxt-resource
                        class="d-none d-md-block"
                        :src="'images/about-page/giftcard-desktop-01.gif'"
                        alt="Product categories"
                    />
                    <nuxt-resource
                        class="d-md-none"
                        :src="'images/about-page/giftcard-mobile-01.gif'"
                        alt="Product categories mobile"
                    />
                </UIGridCol>
            </UIGridRow>
        </UIGridContainer>
        <AboutStatisticsDisplay :reviews="reviewsAmount" :products="242" :team-members="56" />
        <UIGridContainer>
            <UIGridRow class="top-spacing">
                <UIGridCol md="7" class="my-auto">
                    <div class="info-text">
                        <h2 class="mt-0">
                            {{ translate('paragraph2.title') }}
                        </h2>
                        <p v-html="translate('paragraph2.content')" />
                    </div>
                </UIGridCol>
                <UIGridCol md="5" class="mt-5 mt-md-0 pl-0 pl-md-auto">
                    <div class="circle-image">
                        <UIImage
                            class="office-image width-adjust"
                            src="images/about-page/about-office-01.png"
                            :resource="true"
                            :width="411"
                            alt="Office image 1"
                        />
                        <nuxt-resource
                            class="hexagon blue"
                            src="images/about-page/hexagon-blue.svg"
                            alt="Blue hexagon"
                        />
                    </div>
                </UIGridCol>
            </UIGridRow>
        </UIGridContainer>
        <div class="background top-spacing large">
            <UIGridContainer>
                <UIGridRow>
                    <UIGridCol md="6" class="justify-content-center align-items-center d-md-flex d-lg-block">
                        <div class="circle-image top-offset">
                            <UIImage
                                class="office-image"
                                src="images/about-page/about-office-02.png"
                                :resource="true"
                                :width="482"
                                als="Office image 2"
                            />
                            <nuxt-resource
                                class="hexagon red"
                                src="images/about-page/hexagon-pink.svg"
                                alt="Blue hexagon"
                            />
                        </div>
                    </UIGridCol>
                    <UIGridCol md="6" class="padding-bottom pb-lg-0 story-text pt-md-5 pt-lg-0">
                        <div class="w-md-75 text-white ml-lg-5">
                            <h2>{{ translate('paragraph3.title') }}</h2>
                            <p>{{ translate('paragraph3.content1') }}</p>
                            <p v-html="translate('paragraph3.content2')" />
                        </div>
                    </UIGridCol>
                </UIGridRow>
            </UIGridContainer>
        </div>
        <AboutImageExposition />
        <AboutTimeline />
        <AboutTrustPilot v-if="productReviews.length" :reviews="productReviews" />
        <AboutInstagramFeed v-if="instagramPosts" :posts="instagramPosts" />
        <AboutArticles class="about-articles" :articles="articles">
            {{ translate('aboutarticles.info') }}
        </AboutArticles>
    </div>
</template>

<script>
import { UIGridContainer, UIGridRow, UIGridCol, UIImage } from '@dundle/ui/components';
import { mapGetters } from 'vuex';
import {
    AboutBanner,
    AboutStatisticsDisplay,
    AboutImageExposition,
    AboutTimeline,
    AboutTrustPilot,
    AboutArticles,
    AboutInstagramFeed,
} from '~/components/about';
import TranslationMixin from '~/mixins/TranslationMixin';

export default {
    components: {
        UIGridContainer,
        AboutBanner,
        UIGridRow,
        UIGridCol,
        AboutStatisticsDisplay,
        AboutImageExposition,
        AboutTimeline,
        AboutTrustPilot,
        AboutArticles,
        UIImage,
        AboutInstagramFeed,
    },
    mixins: [TranslationMixin],
    async asyncData({ app }) {
        const arrayProductIds = await app.$api.brand.getProductIds('amazon');

        const joinedProductIds = [3, ...arrayProductIds].slice(0, 49).join(',');

        let productReviews = [];
        try {
            productReviews = (await app.$api.trustpilot.getProductReviews(joinedProductIds, app.$locale.language, 1))
                .productReviews;
        } catch (error) {
            app.$sentry.captureException(error);
        }

        const articles = await app.$api.article.getArticleByBrand('about-dundle', 'en', 4);

        let instagramPosts;

        try {
            instagramPosts = (await app.$api.instagram.getPosts()).data;
        } catch (error) {
            console.warn('Instagram posts could not be loaded, please check if the API is still valid.');
        }

        if (instagramPosts) {
            instagramPosts.forEach(post => {
                if (post.media_type === 'VIDEO') {
                    post.media_url = '';
                }
            });
        }

        return {
            productReviews,
            articles,
            instagramPosts,
        };
    },

    data() {
        return {
            statistics: {
                products: 242,
                employees: 56,
            },
        };
    },
    computed: {
        ...mapGetters({
            reviewsAmount: 'trustpilot/getReviewTotal',
        }),
    },
    head() {
        return {
            title: this.translate('meta.title'),
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.translate('meta.description'),
                },
                {
                    hid: 'robots',
                    name: 'robots',
                    content: this.$locale.isTarget() ? 'index, follow' : 'noindex, nofollow',
                },
            ],
            link: [
                {
                    rel: 'stylesheet',
                    type: 'text/css',
                    href: 'https://fonts.googleapis.com/css2?family=Heebo:wght@700&display=swap',
                },
            ],
            bodyAttrs: {
                class: 'parallax',
            },
        };
    },
    meta: {
        footer: 'version-magazine',
    },
};
</script>

<style lang="scss" scoped>
.top-spacing {
    margin-top: 4rem;
    &.large {
        margin-top: 10rem;
        @media (min-width: 576px) and (max-width: 768px) {
            margin-top: 4rem;
        }
    }
    @media screen and (min-width: 768px) {
        margin-top: 6.7rem;
    }

    @media (min-width: 576px) and (max-width: 768px) {
        margin-top: 4rem;
    }
}

.padding-bottom {
    padding-bottom: 4rem;
}

.office-image {
    border-radius: $border-radius-default;
    &.width-adjust {
        @media screen and (max-width: 768px) {
            max-width: 80%;
            border-radius: 0 $border-radius-default $border-radius-default 0;
        }
    }

    @media (min-width: 576px) and (max-width: 768px) {
        min-width: 300px;
    }
}

.hexagon {
    position: absolute;
    // Enable when parallax is fixed
    // transform: translateZ(1px) scale(0.9);
    &.blue {
        top: 80px;
        left: 100px;
        width: 60%;
        @media screen and (min-width: 768px) {
            left: 180px;
            top: 150px;
            width: 75%;
        }

        @media (min-width: 576px) and (max-width: 768px) {
            width: 100%;
            left: 115px;
            top: 100px;
        }
    }
    &.red {
        width: 70%;
        right: -10%;
        top: 60px;
        @media screen and (min-width: 768px) {
            right: -6%;
            top: 80px;
        }

        @media (min-width: 576px) and (max-width: 768px) {
            width: 94%;
            right: -15%;
            top: 25px;
        }
    }
}

.circle-image {
    position: relative;
    // Enable when parallax is fixed
    // transform-style: preserve-3d;
    width: 100%;

    @media (min-width: 576px) and (max-width: 768px) {
        width: 80%;
    }
}

.background {
    background-color: $color-about-bg-pink;
}

.info-text {
    @media screen and (min-width: 768px) {
        max-width: 90%;
    }
    @media (min-width: 576px) and (max-width: 768px) {
        max-width: 100%;
    }
}

.text-white h2 {
    margin-top: 0 !important;
    color: $color-text-inverted !important;
}

.top-offset {
    top: -20%;

    @media (min-width: 576px) and (max-width: 768px) {
        top: auto;
    }
}

.info-text {
    p {
        line-height: 1.467rem;
    }

    h1,
    h2 {
        margin-bottom: 0;
    }
}

.story-text {
    display: flex;
    position: relative;
    justify-content: center;
    align-items: center;

    ::v-deep b {
        color: $color-text-inverted;
    }
}

.about {
    // Fix for horizontal scrolling on IOS Safari
    overflow-x: hidden;
}

.about-articles {
    margin-top: 3.6rem;
    @media screen and (min-width: 768px) {
        margin-top: 8rem;
    }
}
</style>

<i18n src="@dundle/locale/data/articles.json"></i18n>
<i18n></i18n>
